"use client";
import { useCompositeScopedContext } from "./composite-context.js";
import { useSeparator } from "../separator/separator.js";
import { createElement, createHook, forwardRef } from "@ariakit/react-utils";
import { invariant } from "@ariakit/utils";
import { useStoreState } from "@ariakit/react-store";
//#region src/composite/composite-separator.tsx
const TagName = "hr";
/**
* Returns props to create a `CompositeSeparator` component.
* @see https://ariakit.com/components/composite
* @example
* ```jsx
* const store = useCompositeStore();
* const props = useCompositeSeparator({ store });
* <Composite store={store}>
*   <CompositeItem>Item 1</CompositeItem>
*   <Role {...props} />
*   <CompositeItem>Item 2</CompositeItem>
* </Composite>
* ```
*/
const useCompositeSeparator = createHook(function useCompositeSeparator({ store, orientation: orientationProp, ...props }) {
	const context = useCompositeScopedContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "CompositeSeparator must be wrapped in a Composite component.");
	const orientation = useStoreState(store, (state) => orientationProp ?? (state.orientation === "horizontal" ? "vertical" : "horizontal"));
	props = useSeparator({
		...props,
		orientation
	});
	return props;
});
/**
* Renders a divider between
* [`CompositeItem`](https://ariakit.com/reference/composite-item) elements.
* @see https://ariakit.com/components/composite
* @example
* ```jsx {4}
* <CompositeProvider>
*   <Composite>
*     <CompositeItem>Item 1</CompositeItem>
*     <CompositeSeparator />
*     <CompositeItem>Item 2</CompositeItem>
*   </Composite>
* </CompositeProvider>
* ```
*/
const CompositeSeparator = forwardRef(function CompositeSeparator(props) {
	return createElement(TagName, useCompositeSeparator(props));
});
//#endregion
export { CompositeSeparator, useCompositeSeparator };

//# sourceMappingURL=composite-separator.js.map